import { PLATFORM_CONFIG } from '../config/platform';
// 类型专用导入（`import type` 会被编译期擦除 ⇒ 不构成 api ↔ store 的运行期循环依赖）。
// [2026-10-08] 档位词的**单一真源**在 `stores/userStore.ts` 的 `AccountTier`：
// 这里曾自带一份只含 `'FREE' | 'PRO' | 'ENTERPRISE'` 的三值联合，与 store 的四值
// （多一个 `ULTRA`）**非满射** —— `ULTRA` 永远不可能从接口里出来，
// 而调用侧靠 `as any` 强转把这道矛盾盖住了。两处各持一份 = 两本账，改为一处。
import type { AccountTier } from '../stores/userStore';

export interface UserProfile {
    id: string;
    email: string;
    displayName: string;
    avatar?: string;
}

export interface SubscriptionInfo {
    /** 单一真源 = `stores/userStore.ts` 的 `AccountTier`（此前这里自带一份三值联合，与它非满射）。 */
    tier: AccountTier;
    status: 'active' | 'canceled' | 'past_due' | 'trialing';
    currentPeriodEnd: string;
    features: string[];
    limits: {
        maxModelSizeGB: number;
        maxConcurrentInferences: number;
        apiRateLimit: number;
    };
}

export interface PricingFeature {
    label: string;
    included: boolean;
    tooltip?: string;
}

export interface PricingTier {
    id: string;
    name: string;
    tagline: string;
    monthly_price: number;
    annual_price: number;
    original_monthly: number;
    features: PricingFeature[];
    highlighted: boolean;
    cta_label: string;
    badge?: string;
}

export interface ValueChainDiscount {
    role: string;
    gravity_score: number;
    discount_percent: number;
    reason: string;
}

export interface PromoInfo {
    message: string;
    expires_at?: string;
    code?: string;
}

export interface PricingResponse {
    region: string;
    currency: string;
    currency_symbol: string;
    ppp_factor: number;
    tiers: PricingTier[];
    value_chain_discount?: ValueChainDiscount;
    promo?: PromoInfo;
}

export interface BalanceInfo {
    available: number;
    frozen: number;
    currency: string;
}

export interface PaymentIntent {
    id: string;
    stripeSessionId?: string;
    alipayQrCode?: string;
    wechatQrCode?: string;
    amount: number;
    currency: string;
}

// --- Morphic Evolution Types ---
export interface DeathmatchRequest {
    population_size: number;
    rounds: number;
    survival_rate: number;
    chaos_input: any;
}

export interface DeathmatchResult {
    round_results: RoundSummary[];
    alpha_pipeline: MorphicPipeline;
    total_generations: number;
    /**
     * [2026-09-18] 适应度**来源**：后端 `/morphic/darwinism` 自 2026-09-13 起返回
     * `"heuristic_rule_table"`（确定性规则加分表，**非实测**），09-18 又补了 `executed`。
     * 此前前端类型里没有这两个字段 ⇒ 消费方（如 MorphicEvolutionPanel）无从标注，
     * 把 `best_fitness` 当成"这一轮进化的实测质量"显示。缺失（旧内核）按非实测处理。
     */
    fitness_source?: string;
    /** 这些管道**没有被执行过**（生成 + 规则打分）。接入真实执行后由后端置 true。 */
    executed?: boolean;
}

export interface RoundSummary {
    generation: number;
    best_fitness: number;
    avg_fitness: number;
    survivors: number;
}

export interface MorphicPipeline {
    name: string;
    steps: PipelineStep[];
}

export interface PipelineStep {
    id: string;
    engine: string;
    input_map: any;
}

class PlatformApiClient {
    private accessToken: string | null = null;

    constructor() {
        this.accessToken = localStorage.getItem('platform_access_token');
    }

    // @ts-ignore - retained for future use
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const url = `${PLATFORM_CONFIG.primaryUrl}/v1${endpoint}`;
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...(this.accessToken && { 'Authorization': `Bearer ${this.accessToken}` }),
            ...options.headers,
        };

        try {
            const response = await fetch(url, { ...options, headers });
            if (!response.ok) {
                // Mock Failover logic here if needed
                throw new Error(`Platform API Error: ${response.status}`);
            }
            return response.json();
        } catch (error) {
            console.error('Platform API Request Failed:', error);
            throw error;
        }
    }

    // Auth (Mock)
    async login(email: string): Promise<{ accessToken: string }> {
        // In real impl, this calls POST /auth/login
        // [W3 契约修复] mock token 只留在实例内存，不再写入 platform_access_token——
        // apiBase 的 Bearer pick 会把它当真凭据上送，制造持续 401/403 噪声。
        const mockToken = `mock_token_${btoa(email)}`;
        this.accessToken = mockToken;
        return { accessToken: mockToken };
    }

    /**
     * [2026-10-08 授权链订正] 以下三个方法此前**返回硬编码的假数据**，而**没有任何调用方**
     * （`syncWithPlatform` / `upgradeSubscription` 全仓 0 调用点）。
     *
     * 这种"零消费者 + 假返回值"是最坏的一种桩（本仓已记录该形态）：
     * 一旦有人把升级界面接上去，用户会拿到
     * `stripeSessionId: 'cs_test_123'`、`alipayQrCode: 'https://example.com/qr/alipay'`
     * —— 一张**看起来像付款单、实际不存在**的收据；而 `getBalance()` 会画出 `1250.50 USD`，
     * `getUserProfile()` 会写出 `demo@antigravity.ai / Commander Shepard`。
     * 真源其实都在后端（`/api/v2/subscription/user/:user_id` 有 `require_self_or_billing_admin`
     * 鉴权、`/v1/landing/pricing` 也已接真），缺的是装配 —— 那属跨域排期。
     *
     * 处置：**响亮失败**（稳定码 + 说明），不再返回假收据。
     * 未接线的能力要能被立刻认出来，而不是被当成"已经能用"。
     */
    private notWired(op: string): never {
        throw new Error(
            `platform_err:not_wired:${op} — 该平台接口尚未接线（真源在后端：`
            + `GET /api/v2/subscription/user/:user_id / /v1/landing/pricing）。`
            + `本方法此前返回硬编码假数据，已按"桩要响亮失败"纪律改为抛错。`,
        );
    }

    // User Profile
    async getUserProfile(): Promise<UserProfile> {
        return this.notWired('user_profile');
    }

    // Subscription
    async getSubscription(): Promise<SubscriptionInfo> {
        // 曾经的实现是 `localStorage.getItem('account_tier')` 原样回写 —— 「循环自证」：
        // 看起来有链路，其实永不产生新信息（见 stores/userStore.ts 的授权链订正注释）。
        return this.notWired('subscription');
    }

    // Pricing Tiers
    async getPricingTiers(locale?: string, region?: string, userId?: string): Promise<PricingResponse> {
        const queryParams = new URLSearchParams();
        if (locale) queryParams.append('locale', locale);
        if (region) queryParams.append('region', region);
        if (userId) queryParams.append('user_id', userId);
        
        const queryString = queryParams.toString();
        const endpoint = queryString ? `/landing/pricing?${queryString}` : '/landing/pricing';
        
        try {
            return await this.request<PricingResponse>(endpoint);
        } catch (e) {
            console.error('[Platform API] Failed to fetch pricing tiers, falling back to empty response', e);
            throw e;
        }
    }

    // Balance
    async getBalance(): Promise<BalanceInfo> {
        // 曾经的实现返回硬编码 `available: 1250.50 USD`（**假余额**）。
        // 见上方 notWired 的说明：零消费者 + 假返回值 = 最危险的桩。
        return this.notWired('balance');
    }

    // Upgrade
    async upgradeSubscription(tier: string, paymentMethod: 'stripe' | 'alipay' | 'wechat'): Promise<PaymentIntent> {
        // 曾经的实现返回编造的支付单：`id: pi_xxxx` / `stripeSessionId: 'cs_test_123'` /
        // `alipayQrCode: 'https://example.com/qr/alipay'`。**涉及钱不许编**（同族规则：
        // EcommerceControlMatrixPanel 的三张金额卡已改成 `—`）。
        void tier; void paymentMethod;
        return this.notWired('upgrade_subscription');
    }

    // Morphic Evolution (Darwinism)
    async runDarwinism(config: DeathmatchRequest): Promise<DeathmatchResult> {
        return this.request<DeathmatchResult>('/morphic/darwinism', {
            method: 'POST',
            body: JSON.stringify(config)
        });
    }

    // --- Ops & Super Guard (L99) ---
    async opsInitiateApoptosis(payload: { identity: string }): Promise<any> {
        return this.request<any>('/ops/apoptosis/purge', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    async opsChaosIgnite(payload: { level: string, [key: string]: any }): Promise<any> {
        return this.request<any>('/ops/chaos/ignite', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    async opsChaosStop(): Promise<any> {
        return this.request<any>('/ops/chaos/stop', {
            method: 'POST'
        });
    }
}

export const platformApi = new PlatformApiClient();
